import { type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { gripWhy, useCardMove } from '../../lib/card-move';
import type { MoveSubject } from '../../lib/move-items';
import { restSeconds, useRest } from '../../lib/move-rest';
import { MoveIcon } from './MoveIcon';

// The Move button on every movable job, epic and group (#6771, prototype moveBtn
// `:1682-1685`): a swap icon that opens Move to under its row; the single-job layout uses the
// labelled form, "Move" (`:2188`). The keyboard's way to move work: Move to's options are
// buttons, and its search takes Enter.

export function MoveButton(props: {
  readonly subject: MoveSubject;
  readonly labelled?: boolean;
}): JSX.Element {
  const m = useCardMove();
  // Disabled for the grip's reasons (the move limit, the page's lock): still focusable, says why.
  const why = (): string | null => (m === null ? null : gripWhy(m));
  // Resting after a move (#6772, prototype `.mvbtn.cooling`): the accent at 0.55 opacity.
  const rest = useRest(() => props.subject);
  return (
    <button
      type="button"
      class="mission-ctl__btn move-btn"
      classList={{
        'mission-ctl__btn--labelled': props.labelled === true,
        'move-rest--live': rest() !== null,
      }}
      data-move-btn={props.subject.key}
      aria-expanded={m?.page.menuKey() === props.subject.key}
      aria-label={t('fleet.move.grip_label', { label: props.subject.label })}
      aria-disabled={why() === null ? undefined : 'true'}
      title={
        why() ??
        (() => {
          const r = rest();
          return r === null
            ? t('fleet.move.button_title')
            : t('fleet.move.ring_title', { seconds: restSeconds(r, m?.page.clock() ?? 0) });
        })()
      }
      onClick={() => {
        if (m !== null && why() === null) m.page.openMenu(props.subject);
      }}
    >
      <MoveIcon glyph="swap" />
      <Show when={props.labelled === true}>
        <span>{t('fleet.move.button')}</span>
      </Show>
    </button>
  );
}
